// /en/compare/$topic — English mirror of /compare/$topic (Phase P29-EN)
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import type { ComparisonTopic } from "@/data/comparisons";
import { Check, X, Minus, ArrowRight, Sparkles } from "lucide-react";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedServiceLocations } from "@/components/RelatedServiceLocations";
import CompareTripleKeyLinks from "@/components/CompareTripleKeyLinks";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/compare/$topic")({
  loader: async ({ params }) => {
    const { COMPARISONS, getComparisonBySlug } = await import("@/data/comparisons");
    const topic = getComparisonBySlug(params.topic);
    if (!topic) throw notFound();
    return { topic, all: COMPARISONS };
  },
  head: ({ loaderData }) => {
    const topic = loaderData?.topic;
    if (!topic) return {};
    const enPath = `/en/compare/${topic.slug}`;
    const thPath = `/compare/${topic.slug}`;
    const url = `${SITE}${enPath}`;
    const title = `${topic.titleEn} · Comparison 2026 | NYC Legal & Notary`;
    const desc = topic.descEn;
    return {
      meta: [
        { title: ctaTitle(title, "/en/compare/$topic", "en") },
        { name: "description", content: ctaDesc(desc, "/en/compare/$topic", "en")},
        { name: "keywords", content: `${topic.intentKeyword}, NYC vs ${topic.altLabelEn}, comparison, Thailand` },
        { property: "og:title", content: ctaTitle(title, "/en/compare/$topic", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/compare/$topic", "en")},
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(thPath, enPath),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2", ".recommendation"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              inLanguage: "en",
              mainEntity: topic.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "@id": `${url}#article`,
              headline: title,
              description: desc,
              author: { "@type": "Organization", "@id": `${SITE}#org`, name: "NYC Legal & Notary Service" },
              publisher: {
                "@type": "Organization",
                name: "NYC Legal & Notary Service",
                logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
              },
              datePublished: "2026-01-15",
              dateModified: new Date().toISOString().slice(0, 10),
              inLanguage: "en",
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              about: [
                { "@type": "Thing", name: "NYC Legal & Notary Service" },
                { "@type": "Thing", name: topic.altLabelEn },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "@id": `${url}#comparison`,
              name: `Comparison criteria: ${topic.titleEn}`,
              numberOfItems: topic.rows.length,
              itemListElement: topic.rows.map((r, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: r.criterionEn,
                description: `NYC: ${r.nycEn} | ${topic.altLabelEn}: ${r.altEn} | Winner: ${r.winner === "nyc" ? "NYC" : r.winner === "alt" ? topic.altLabelEn : "Tie"}`,
              })),
            }),
          },
          // Round 12 — Recommendation node removed (Google parses
          // itemReviewed + reviewBody as a Review snippet, disallowed for
          // self-published marketing comparisons).
          // Round 11 — Removed ClaimReview map (GSC policy: ClaimReview is
          // reserved for IFCN-signatory fact-checking organisations; marketing
          // comparison pages must not emit it).
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Compare", item: `${SITE}/en/compare` },
                { "@type": "ListItem", position: 3, name: topic.titleEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: EnComparePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Comparison not found</h1>
      <Link to="/en/compare" className="mt-4 inline-block text-gold underline">Back to all comparisons</Link>
    </main>
  ),
});

function EnComparePage() {
  const { topic, all } = Route.useLoaderData() as { topic: ComparisonTopic; all: ComparisonTopic[] };
  const nycWins = topic.rows.filter((r) => r.winner === "nyc").length;
  const altWins = topic.rows.filter((r) => r.winner === "alt").length;
  const ties = topic.rows.filter((r) => r.winner === "tie").length;

  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <nav className="text-[11px] text-white/60">
            <Link to="/en" className="hover:text-white">Home</Link> ·{" "}
            <Link to="/en/compare" className="hover:text-white">Compare</Link>
          </nav>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Sparkles className="h-3.5 w-3.5" /> Comparison 2026
          </div>
          <h1 className="aeo-speakable mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {topic.titleEn}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{topic.descEn}</p>

          <div className="mt-6 grid grid-cols-3 gap-3 text-center sm:max-w-md">
            <div className="rounded-xl border border-emerald-400/30 bg-emerald-600/10 px-3 py-2">
              <div className="text-2xl font-extrabold text-emerald-400">{nycWins}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">NYC wins</div>
            </div>
            <div className="rounded-xl border border-white/20 bg-white/5 px-3 py-2">
              <div className="text-2xl font-extrabold text-white/80">{ties}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">Tie</div>
            </div>
            <div className="rounded-xl border border-slate-400/30 bg-white/5 px-3 py-2">
              <div className="text-2xl font-extrabold text-white/70">{altWins}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">{topic.altLabelEn}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-amber-50/50">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">TL;DR Recommendation</h2>
          <p className="recommendation aeo-speakable mt-3 text-base leading-relaxed text-slate-800 sm:text-lg">
            {topic.recommendationEn}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">Side-by-side comparison</h2>
        <p className="mt-2 text-sm text-slate-600">{topic.rows.length} criteria · Last updated {new Date().toISOString().slice(0, 10)}</p>

        <p className="mt-4 text-[11px] font-semibold text-slate-500 sm:hidden" aria-hidden="true">Swipe →</p>
        <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 sm:mt-6">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-bold text-slate-700">Criterion</th>
                <th className="px-4 py-3 text-left font-bold text-emerald-700">NYC Legal & Notary</th>
                <th className="px-4 py-3 text-left font-bold text-slate-600">{topic.altLabelEn}</th>
                <th className="px-4 py-3 text-center font-bold text-slate-700">Winner</th>
              </tr>
            </thead>
            <tbody>
              {topic.rows.map((r, i) => (
                <tr key={i} id={`row-${i + 1}`} className="border-t border-slate-200 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-semibold text-slate-800">{r.criterionEn}</td>
                  <td className="px-4 py-3 text-slate-700">{r.nycEn}</td>
                  <td className="px-4 py-3 text-slate-700">{r.altEn}</td>
                  <td className="px-4 py-3 text-center">
                    {r.winner === "nyc" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700">
                        <Check className="h-3 w-3" /> NYC
                      </span>
                    ) : r.winner === "alt" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-1 text-[11px] font-bold text-slate-700">
                        <X className="h-3 w-3" /> Alt
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-700">
                        <Minus className="h-3 w-3" /> Tie
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep">Frequently asked questions</h2>
          <div className="mt-6 space-y-4">
            {topic.faqs.map((f, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="font-bold text-navy-deep">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-700">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">Other comparisons</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {all.filter((c) => c.slug !== topic.slug)
            .slice(0, 6)
            .map((c) => (
              <Link
                key={c.slug}
                to="/en/compare/$topic"
                params={{ topic: c.slug }}
                className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <span className="font-semibold text-navy-deep group-hover:text-amber-600">{c.titleEn}</span>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
        </div>

        <p className="mt-8 text-sm text-slate-600">
          ภาษาไทย? <Link to="/compare/$topic" params={{ topic: topic.slug }} className="font-semibold text-amber-700 underline">/compare/{topic.slug}</Link>
        </p>
      </section>

      <CompareTripleKeyLinks mode="en" topicTitleTh={topic.titleTh} topicTitleEn={topic.titleEn} />
      <RelatedServiceLocations comparisonSlug={topic.slug} locale="en" />
      <TrustLayer variant="compact" />
    </main>
  );
}
